import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, User, Check, X } from 'lucide-react';
import SpinWheel from './SpinWheel';
import { checkRankUp } from '../utils/points';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const SimpleDuelGame = ({ user, onLogout }) => {
  const { duelId } = useParams();
  const navigate = useNavigate();
  
  const [duel, setDuel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gamePhase, setGamePhase] = useState('loading'); // loading, wheel, question, feedback, results
  const [currentRound, setCurrentRound] = useState(0);
  const [myAnswers, setMyAnswers] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    fetchDuelData();
  }, [duelId]);

  const fetchDuelData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get(`/duels/${duelId}`);
      const duelData = response.data;
      setDuel(duelData);
      
      // Determine game phase
      const isPlayer1 = user.id === duelData.player1_id;
      
      if (duelData.status === 'completed') {
        setGamePhase('results');
      } else if (isPlayer1 && duelData.status === 'waiting_player1') {
        setGamePhase('wheel');
      } else if (!isPlayer1 && duelData.status === 'waiting_player2') {
        setGamePhase('wheel');
      } else {
        setGamePhase('waiting');
      }
      
    } catch (error) {
      console.error('Error fetching duel:', error);
      const errorMessage = error.response?.data?.detail || 'Error al cargar el duelo';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleWheelResult = async (selectedSpecialty) => {
    // Get the pre-determined question for this round
    try {
      const questionId = duel.round_questions[currentRound];
      const response = await apiClient.get(`/questions/${questionId}`);
      setCurrentQuestion(response.data);
      setGamePhase('question');
    } catch (error) {
      toast.error('Error al cargar pregunta');
    }
  };

  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
    setShowFeedback(true);
  };

  const handleNextQuestion = () => {
    const newAnswers = [...myAnswers, selectedAnswer];
    setMyAnswers(newAnswers);
    
    setSelectedAnswer(null);
    setShowFeedback(false);
    setCurrentQuestion(null);
    
    const nextRound = currentRound + 1;
    setCurrentRound(nextRound);
    
    if (nextRound < 5) {
      setGamePhase('wheel');
    } else {
      submitMyGame(newAnswers);
    }
  };

  const submitMyGame = async (finalAnswers) => {
    try {
      const response = await apiClient.post('/duels/submit-game', {
        duel_id: duelId,
        answers: finalAnswers
      });

      toast.success(`¡Completado! Puntuación: ${response.data.my_score}/5`);
      checkRankUp(response.data);
      setGamePhase('results');
      
      setTimeout(() => {
        fetchDuelData();
      }, 2000);
      
    } catch (error) {
      toast.error('Error al enviar respuestas');
    }
  };

  const abandonDuel = async () => {
    try {
      await apiClient.post(`/duels/abandon/${duelId}`);
      toast.success('Duelo abandonado');
      navigate('/duels');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al abandonar duelo');
    }
  };

  const exitTemporarily = () => {
    toast.info('Puedes volver más tarde para continuar tu turno');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-green-600 font-semibold">Cargando duelo...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md mx-4 text-center">
          <div className="text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex space-x-4 justify-center">
            <button
              onClick={() => navigate('/duels')}
              className="btn-primary bg-gradient-to-r from-green-500 to-teal-500"
            >
              Volver a Duelos
            </button>
            <button
              onClick={fetchDuelData}
              className="btn-secondary"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!duel) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md mx-4 text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Duelo no encontrado</h2>
          <p className="text-gray-600 mb-6">El duelo que buscas no existe o ha sido eliminado.</p>
          <button
            onClick={() => navigate('/duels')}
            className="btn-primary bg-gradient-to-r from-green-500 to-teal-500"
          >
            Volver a Duelos
          </button>
        </div>
      </div>
    );
  }

  const isPlayer1 = user.id === duel.player1_id;
  const myName = isPlayer1 ? duel.player1_name : duel.player2_name;
  const opponentName = isPlayer1 ? duel.player2_name : duel.player1_name;
  const myImage = isPlayer1 ? duel.player1_image : duel.player2_image;
  const opponentImage = isPlayer1 ? duel.player2_image : duel.player1_image;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-teal-50 to-emerald-50">
      {/* Navigation */}
      <nav className="nav-container bg-gradient-to-r from-green-600 to-teal-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/duels')}
                className="flex items-center space-x-2 text-white hover:text-teal-200 transition-colors"
              >
                <ArrowLeft size={20} />
                <span>Duelos</span>
              </button>
              <h1 className="text-xl font-bold text-white">Duelo vs {opponentName}</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 text-white bg-white bg-opacity-20 px-3 py-2 rounded-lg hover:bg-opacity-30 transition-all font-semibold"
              >
                <span>Menú Principal</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Players Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {myImage ? (
                <img
                  src={myImage}
                  alt={myName}
                  className="w-16 h-16 rounded-full object-cover border-4 border-green-300"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-green-600 border-4 border-green-300 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">
                    {myName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div>
                <h3 className="text-lg font-semibold">{myName} (Tú)</h3>
                <div className="text-sm text-gray-600">Estado: {duel.status}</div>
              </div>
            </div>
            
            <div className="text-4xl">⚔️</div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <h3 className="text-lg font-semibold">{opponentName}</h3>
                <div className="text-sm text-gray-600">Oponente</div>
              </div>
              {opponentImage ? (
                <img
                  src={opponentImage}
                  alt={opponentName}
                  className="w-16 h-16 rounded-full object-cover border-4 border-teal-300"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-teal-600 border-4 border-teal-300 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">
                    {opponentName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Game Content */}
        {gamePhase === 'wheel' && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-green-800 mb-2">
                Ronda {currentRound + 1} de 5
              </h3>
              <p className="text-gray-600">
                Gira la rueda para seleccionar la especialidad
              </p>
            </div>
            
            {/* Action buttons */}
            <div className="flex justify-center space-x-4 mb-6">
              <button
                onClick={exitTemporarily}
                className="btn-secondary bg-gray-100 text-gray-600 hover:bg-gray-200"
              >
                🚪 Salir Temporalmente
              </button>
              <button
                onClick={abandonDuel}
                className="btn-secondary bg-red-100 text-red-600 hover:bg-red-200"
              >
                ❌ Abandonar Duelo
              </button>
            </div>
            
            <SpinWheel 
              onResult={handleWheelResult}
              targetSpecialty={duel.round_specialties[currentRound]}
            />
          </div>
        )}

        {gamePhase === 'question' && currentQuestion && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="mb-6">
              <div className="bg-gradient-to-r from-green-100 to-teal-100 p-4 rounded-lg mb-6">
                <h4 className="text-lg font-semibold text-green-800">
                  📚 {duel.round_specialties[currentRound]}
                </h4>
              </div>
              
              <h2 className="text-xl font-semibold text-gray-900 leading-relaxed">
                {currentQuestion.question_text}
              </h2>
            </div>

            <div className="space-y-3 mb-6">
              {['A', 'B', 'C', 'D'].map((option) => {
                const optionText = currentQuestion[`option_${option.toLowerCase()}`];
                const isSelected = selectedAnswer === option;
                const isCorrect = currentQuestion.correct_answer === option;
                
                let optionClass = 'quiz-option cursor-pointer';
                if (showFeedback) {
                  if (isCorrect) {
                    optionClass += ' correct';
                  } else if (isSelected && !isCorrect) {
                    optionClass += ' incorrect';
                  }
                } else if (isSelected) {
                  optionClass += ' selected';
                }

                return (
                  <div
                    key={option}
                    className={optionClass}
                    onClick={() => !showFeedback && handleAnswerSelect(option)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="font-semibold text-lg mr-3 text-green-600">
                          {option}.
                        </span>
                        <span>{optionText}</span>
                      </div>
                      {showFeedback && (
                        <div className="ml-4">
                          {isCorrect ? (
                            <Check size={24} className="icon-check" />
                          ) : (
                            isSelected && !isCorrect && <X size={24} className="icon-x" />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {!showFeedback && !selectedAnswer && (
              <div className="text-center text-gray-500 py-4">
                Selecciona tu respuesta
              </div>
            )}

            {showFeedback && (
              <div className="feedback-box">
                <h4>
                  {selectedAnswer === currentQuestion.correct_answer ? (
                    <span className="flex items-center text-green-600">
                      <Check size={20} className="mr-2" />
                      ¡Correcto!
                    </span>
                  ) : (
                    <span className="flex items-center text-red-600">
                      <X size={20} className="mr-2" />
                      Incorrecto
                    </span>
                  )}
                </h4>
                <p><strong>Respuesta correcta:</strong> {currentQuestion.correct_answer}</p>
                <p><strong>Explicación:</strong> {currentQuestion.explanation}</p>
                <p><strong>Referencia:</strong> {currentQuestion.reference}</p>
                
                <div className="mt-6">
                  <button
                    onClick={handleNextQuestion}
                    className="btn-primary bg-gradient-to-r from-green-500 to-teal-500"
                  >
                    {currentRound < 4 ? 'Siguiente Pregunta' : 'Finalizar Duelo'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {gamePhase === 'waiting' && (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="animate-pulse mb-6">
              <div className="text-6xl mb-4">⏳</div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Esperando turno...
            </h3>
            <p className="text-gray-600 mb-6">
              Tu oponente debe completar primero
            </p>
            <button
              onClick={() => navigate('/duels')}
              className="btn-secondary"
            >
              Volver a Duelos
            </button>
          </div>
        )}

        {gamePhase === 'results' && (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            {duel.status === 'completed' && duel.player1_completed && duel.player2_completed ? (
              <>
                {/* Duel Completed - Show Results */}
                <div className="text-6xl mb-6">🏆</div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  ¡Duelo Completado!
                </h3>
                
                {/* Scoreboard */}
                <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-xl p-6 mb-8">
                  <div className="grid grid-cols-3 items-center gap-4">
                    {/* Player 1 */}
                    <div className="text-center">
                      <div className="flex justify-center mb-2">
                        {duel.player1_image ? (
                          <img
                            src={duel.player1_image}
                            alt={duel.player1_name}
                            className="w-16 h-16 rounded-full object-cover border-4 border-green-300"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-green-600 border-4 border-green-300 flex items-center justify-center">
                            <span className="text-2xl font-bold text-white">
                              {duel.player1_name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        {duel.winner_id === duel.player1_id && !duel.forfeit && (
                          <div className="absolute -mt-2 -ml-2">
                            <span className="text-2xl">🏆</span>
                          </div>
                        )}
                      </div>
                      <p className="font-semibold">{duel.player1_name}</p>
                      <p className="text-2xl font-bold text-green-600">{duel.player1_score}</p>
                    </div>
                    
                    {/* VS */}
                    <div className="text-center">
                      <div className="text-4xl font-bold text-gray-400">VS</div>
                      <div className="text-sm text-gray-500 mt-2">
                        {duel.forfeit ? 'Por abandono' : `${Math.max(duel.player1_score, duel.player2_score)}/5`}
                      </div>
                    </div>
                    
                    {/* Player 2 */}
                    <div className="text-center">
                      <div className="flex justify-center mb-2">
                        {duel.player2_image ? (
                          <img
                            src={duel.player2_image}
                            alt={duel.player2_name}
                            className="w-16 h-16 rounded-full object-cover border-4 border-teal-300"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-teal-600 border-4 border-teal-300 flex items-center justify-center">
                            <span className="text-2xl font-bold text-white">
                              {duel.player2_name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        {duel.winner_id === duel.player2_id && !duel.forfeit && (
                          <div className="absolute -mt-2 -ml-2">
                            <span className="text-2xl">🏆</span>
                          </div>
                        )}
                      </div>
                      <p className="font-semibold">{duel.player2_name}</p>
                      <p className="text-2xl font-bold text-teal-600">{duel.player2_score}</p>
                    </div>
                  </div>
                </div>
                
                {/* Winner Declaration */}
                {duel.winner_id && (
                  <div className="mb-6">
                    <div className="text-4xl mb-4">🏆</div>
                    <h4 className="text-2xl font-bold text-green-600 mb-2">
                      ¡{duel.winner_id === duel.player1_id ? duel.player1_name : duel.player2_name} Gana!
                    </h4>
                    {duel.forfeit && (
                      <p className="text-gray-600">Victoria por abandono del oponente</p>
                    )}
                  </div>
                )}
                
                {!duel.winner_id && duel.player1_score === duel.player2_score && (
                  <div className="mb-6">
                    <div className="text-4xl mb-4">🤝</div>
                    <h4 className="text-2xl font-bold text-gray-600 mb-2">
                      ¡Empate!
                    </h4>
                    <p className="text-gray-600">Ambos jugadores obtuvieron {duel.player1_score} puntos</p>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Game submitted but waiting for opponent */}
                <div className="text-6xl mb-4">⏳</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  ¡Juego Completado!
                </h3>
                <p className="text-gray-600 mb-4">
                  Has terminado tu turno. Espera a que tu oponente complete el suyo.
                </p>
                <p className="text-green-600 font-semibold mb-6">
                  Tu puntuación: {isPlayer1 ? duel.player1_score : duel.player2_score}/5
                </p>
              </>
            )}
            
            <button
              onClick={() => navigate('/duels')}
              className="btn-primary bg-gradient-to-r from-green-500 to-teal-500"
            >
              Volver a Duelos
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleDuelGame;